import * as React from 'react';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useGetPricingPlanDetails } from 'src/features/pricingPlans/api/useGetPricingPlanDetails';
import { PricingPlanFormModal } from 'src/features/pricingPlans/components/PricingPlanFormModal';
import { TIME_TO_LEAVE_OPTIONS } from 'src/features/pricingPlans/types';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import DetailsSection from 'src/features/reservations/components/DetailsSection';
import { useMediaQuery } from 'src/hooks/use-media-query';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages, generalPriceMessages } from 'src/utils/messages';

function ParkingPricingDetails() {
    const { pricePlanId, parkingId: selectedParkingId } = useParams();
    const { parkingId } = useParams();
    const { data: parkingData } = useGetParking({ parkingId: selectedParkingId! });

    const { formatMessage, formatNumber } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { data, refetch } = useGetPricingPlanDetails({
        parkingId: parkingId || '',
        planId: pricePlanId ? Number(pricePlanId) : null,
        config: { enabled: !!parkingId },
    });
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const permissionManage = useHasPermissions([PERMISSION.PRICING_MANAGE]);
    const isSmallScreen = useMediaQuery('(max-width: 1224px)');

    React.useEffect(() => {
        if (!data || !parkingData) return;
        setTitle(parkingData.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const minutesToClock = React.useCallback((minutes?: number) => {
        if (minutes == null) return '--:--';

        const DAY = 24 * 60;
        const total = ((minutes % DAY) + DAY) % DAY;

        const formattedHours = String(Math.floor(total / 60)).padStart(2, '0');
        const formattedMinutes = String(total % 60).padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}`;
    }, []);

    //TODO: replace hardcoded BGN currency

    const formatPrice = React.useCallback(
        (price: number | undefined) => {
            if (price === undefined) return '-';
            return formatNumber(price / 100, {
                style: 'currency',
                currency: 'BGN',
            });
        },
        [formatNumber],
    );

    const formatSecondsToMinutes = React.useCallback((seconds: number | undefined): string => {
        if (seconds === undefined || isNaN(seconds)) return '-';
        return Math.floor(seconds / 60).toString();
    }, []);

    const additionalPropsForMobile = {
        button: {
            title: formatMessage(pricingPlansMessages.editPricingPlan),
            onClick: () => setIsEditModalOpen(!isEditModalOpen),
            icon: faEdit,
        },
    };

    const timeToLeaveTranslationMapping = {
        [TIME_TO_LEAVE_OPTIONS.NORMAL]: pricingPlansMessages.timeToLeaveNormal,
        [TIME_TO_LEAVE_OPTIONS.FIXED_HOUR]: pricingPlansMessages.timeToLeaveFixedHour,
    };

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/parking-management/${selectedParkingId}/pricing-plans`}>
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(pricingPlansMessages.backPricingPlans)}
                </Link>
            </Button>
            {permissionManage && (
                <PricingPlanFormModal
                    selectedPricingPlan={data}
                    onSubmit={refetch}
                    isDialogOpen={isEditModalOpen}
                    onCloseDialog={() => setIsEditModalOpen(false)}
                />
            )}
            <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                <DetailsSection
                    title={formatMessage(pricingPlansMessages.pricingPlanGlimpse)}
                    data={[
                        { title: formatMessage(pricingPlansMessages.planName), value: data?.plan_name },
                        {
                            title: formatMessage(pricingPlansMessages.initialPrice),
                            value: formatPrice(data?.initial_price),
                        },
                        {
                            title: `${formatMessage(generalPriceMessages.vat)} %`,
                            value: data?.vat ? (data?.vat / 100).toFixed(2) : '-',
                        },
                    ]}
                    {...(isSmallScreen && permissionManage ? additionalPropsForMobile : {})}
                />
                <DetailsSection
                    isSubSection={isSmallScreen}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.freeTime),
                            value: formatSecondsToMinutes(data?.free_time),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.timeToLeave),
                            value: formatSecondsToMinutes(data?.time_to_leave),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.timeToLeaveType),
                            value: data?.time_to_leave_option
                                ? formatMessage(timeToLeaveTranslationMapping[data?.time_to_leave_option])
                                : '',
                        },
                    ]}
                    {...(!isSmallScreen && permissionManage ? additionalPropsForMobile : {})}
                />
            </div>
            <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                <DetailsSection
                    title={formatMessage(pricingPlansMessages.firstPeriod)}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.durationInMinutes),
                            value: formatSecondsToMinutes(data?.first_period[0]?.duration),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.priceTick),
                            value: formatSecondsToMinutes(data?.first_period[0]?.price_tick),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.dueAmount),
                            value: formatPrice(data?.first_period[0]?.due_amount),
                        },
                    ]}
                />
                <DetailsSection
                    title={formatMessage(pricingPlansMessages.secondPeriod)}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.durationInMinutes),
                            value: formatSecondsToMinutes(data?.second_period[0]?.duration),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.priceTick),
                            value: formatSecondsToMinutes(data?.second_period[0]?.price_tick),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.dueAmount),
                            value: formatPrice(data?.second_period[0]?.due_amount),
                        },
                    ]}
                />
            </div>
            <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                <DetailsSection
                    title={formatMessage(pricingPlansMessages.thirdPeriod)}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.priceTick),
                            value: formatSecondsToMinutes(data?.third_period[0]?.price_tick),
                        },
                    ]}
                />
                <DetailsSection
                    isSubSection={isSmallScreen}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.dueAmount),
                            value: formatPrice(data?.third_period[0]?.due_amount),
                        },
                    ]}
                />
            </div>
            <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                <DetailsSection
                    title={formatMessage(pricingPlansMessages.specialPeriod)}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.inUse),
                            value: formatMessage(
                                data?.special_period[0]?.in_use ? generalMessages.yes : generalMessages.no,
                            ),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.periodStart),
                            value: minutesToClock(data?.special_period[0]?.period_start),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.periodEnd),
                            value: minutesToClock(data?.special_period[0]?.period_end),
                        },
                    ]}
                />
                <DetailsSection
                    isSubSection={isSmallScreen}
                    data={[
                        {
                            title: formatMessage(pricingPlansMessages.priceTick),
                            value: formatSecondsToMinutes(data?.special_period[0]?.price_tick),
                        },
                        {
                            title: formatMessage(pricingPlansMessages.dueAmount),
                            value: formatPrice(data?.special_period[0]?.due_amount),
                        },
                    ]}
                />
            </div>
        </>
    );
}

export default ParkingPricingDetails;
